// Piezas visuales de Finanzas. Mismo lenguaje que asesoras y monitoreo: tarjetas blancas con borde
// gray.200, indicadores en mayúsculas y el morado de la marca como acento.
import { Badge, Box, Button, Flex, Heading, IconButton, Text } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { BRAND, PERIOD_STATUS, monthLabel } from 'api/administrador/finance';

export function PageHeader({ title, subtitle, breadcrumb, actions }) {
  return (
    <Flex justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap={4} mb={5} wrap="wrap">
      <Box minW={0}>
        {breadcrumb && (
          <Flex fontSize="sm" color="gray.500" gap={2} mb={2} wrap="wrap">
            {breadcrumb.map((item, index) => (
              <Flex key={item.label} gap={2}>
                {index > 0 && <Text as="span">›</Text>}
                {item.to ? (
                  <Text as={Link} to={item.to} color={BRAND} _hover={{ textDecoration: 'underline' }}>
                    {item.label}
                  </Text>
                ) : (
                  <Text as="span">{item.label}</Text>
                )}
              </Flex>
            ))}
          </Flex>
        )}
        <Heading as="h1" fontSize={{ base: '22px', md: '26px' }} fontWeight="700" color="gray.800">
          {title}
        </Heading>
        {subtitle && (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {subtitle}
          </Text>
        )}
      </Box>
      {actions && (
        <Flex gap={3} wrap="wrap">
          {actions}
        </Flex>
      )}
    </Flex>
  );
}

export function Card({ title, description, actions, children, padding = 5, ...rest }) {
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" {...rest}>
      {(title || actions) && (
        <Flex justify="space-between" align="flex-start" gap={3} px={padding} pt={padding} pb={2} wrap="wrap">
          <Box>
            {title && (
              <Heading as="h2" fontSize="md" fontWeight="600" color="gray.800">
                {title}
              </Heading>
            )}
            {description && (
              <Text fontSize="sm" color="gray.500" mt={1}>
                {description}
              </Text>
            )}
          </Box>
          {actions}
        </Flex>
      )}
      <Box px={padding} pb={padding} pt={title || actions ? 2 : padding}>
        {children}
      </Box>
    </Box>
  );
}

export function KpiTile({ label, value, hint, tone }) {
  const color = { good: 'green.600', bad: 'red.600', warn: 'orange.600' }[tone] || 'gray.800';
  return (
    <Box bg="white" border="1px solid" borderColor={tone === 'bad' ? 'red.200' : 'gray.200'} borderRadius="lg" px={5} py={4} minW={0}>
      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Text>
      <Text fontSize="clamp(17px, 1.7vw, 24px)" fontWeight="700" color={color} mt={1} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={typeof value === 'string' ? value : undefined} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color="gray.500" mt={0.5}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

export function Tag({ children, bg = 'gray.100', color = 'gray.700' }) {
  return (
    <Badge bg={bg} color={color} textTransform="none" fontWeight="600" fontSize="xs" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap">
      {children}
    </Badge>
  );
}

export function PeriodStatusTag({ status }) {
  const s = PERIOD_STATUS[status];
  if (!s) return null;
  return <Tag bg={s.bg} color={s.color}>{s.label}</Tag>;
}

export function EmptyState({ title, description, action }) {
  return (
    <Flex direction="column" align="center" textAlign="center" py={10} px={4} gap={2}>
      <Text fontWeight="600" color="gray.700">
        {title}
      </Text>
      {description && (
        <Text fontSize="sm" color="gray.500" maxW="460px">
          {description}
        </Text>
      )}
      {action && <Box mt={3}>{action}</Box>}
    </Flex>
  );
}

/** ‹ Octubre 2026 › — cambia de mes sin pasar de `max` (hoy) ni de `min`. */
export function MonthStepper({ value, onChange, min, max, label }) {
  const idx = (v) => (v ? v.year * 12 + v.month : null);
  const canPrev = !min || idx(value) > idx(min);
  const canNext = !max || idx(value) < idx(max);
  const step = (delta) => {
    const i = value.year * 12 + (value.month - 1) + delta;
    onChange({ year: Math.floor(i / 12), month: (i % 12) + 1 });
  };
  return (
    <Flex align="center" border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" h="32px">
      <IconButton aria-label="Mes anterior" icon={<FiChevronLeft />} size="sm" variant="ghost" isDisabled={!canPrev} onClick={() => step(-1)} />
      <Text fontSize="sm" fontWeight="600" px={2} whiteSpace="nowrap">
        {label || monthLabel(value.year, value.month)}
      </Text>
      <IconButton aria-label="Mes siguiente" icon={<FiChevronRight />} size="sm" variant="ghost" isDisabled={!canNext} onClick={() => step(1)} />
    </Flex>
  );
}

export function SegmentedButtons({ options, value, onChange, size = 'sm', ariaLabel }) {
  return (
    <Flex role="group" aria-label={ariaLabel} wrap="wrap">
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <Button
            key={o.value}
            size={size}
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            colorScheme={active ? 'purple' : 'gray'}
            variant={active ? 'solid' : 'outline'}
            borderRadius={0}
            borderLeftRadius={i === 0 ? 'md' : 0}
            borderRightRadius={i === options.length - 1 ? 'md' : 0}
            ml={i === 0 ? 0 : '-1px'}
            bg={active ? undefined : 'white'}
          >
            {o.label}
          </Button>
        );
      })}
    </Flex>
  );
}
